import { Question } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApi } from "~/api/provider";
import { Button } from "~/foundation/components/Button";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { formatRelativeTime } from "~/foundation/utils/time";
import { WhatsAppHelp } from "./WhatsAppHelp";

/** `work.whatsapp.status | link | unlink` — bare JSON, no envelope (Docit Work 05 §10). */
interface WhatsAppStatus {
  configured: boolean;
  number: string | null;
  templateConfigured: boolean;
  linked: boolean;
  phone: string | null;
  profileName: string | null;
  linkedAt: string | null;
  windowOpenUntil: string | null;
  connectUrl: string | null;
  connectCode: string | null;
  connectExpiresAt: string | null;
}

const KEY = ["work", "whatsapp", "status"] as const;

/** A number inside a Hebrew (RTL) sentence: isolate it so "+•••1234" keeps its order. */
const ltr = (value: string | null | undefined) => (value ? `\u2066${value}\u2069` : "");

/**
 * Docit Work 05 §10 — connect MY WhatsApp number. Connecting is a one-time code the server mints for
 * the signed-in person; the wa.me link opens WhatsApp with "link <code>" already typed, and the number
 * that SENDS it is bound — nobody can type in someone else's number. While a code is out the status is
 * polled, so the page flips to "Connected" by itself. WhatsApp's 24-hour rule is shown plainly: inside
 * the window questions arrive as cards; outside it a short approved notice (or nothing, without one)
 * until the person writes.
 */
export function WhatsApp() {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{
    url: string;
    code: string | null;
    expiresAt: string | null;
  } | null>(null);
  const search = useSearch({ strict: false }) as { help?: string };
  const [helpOpen, setHelpOpen] = useState(search.help === "setup");

  const { data, isLoading } = useQuery({
    queryKey: KEY,
    queryFn: async () => (await api.research.post("work.whatsapp.status", {})) as WhatsAppStatus,
    // Poll only while a code can still be used — it dies after 15 minutes.
    refetchInterval: (query) =>
      pending &&
      !(query.state.data as WhatsAppStatus | undefined)?.linked &&
      (!pending.expiresAt || Date.parse(pending.expiresAt) > Date.now())
        ? 3000
        : false,
  });

  const link = useMutation({
    mutationFn: async () => (await api.research.post("work.whatsapp.link", {})) as WhatsAppStatus,
    onSuccess: (status) => {
      if (status.connectUrl) {
        setPending({
          url: status.connectUrl,
          code: status.connectCode,
          expiresAt: status.connectExpiresAt,
        });
      }
    },
    onError: () => toast.error(t("WhatsApp could not be reached. Try again in a minute.")),
  });

  const unlink = useMutation({
    mutationFn: async () => (await api.research.post("work.whatsapp.unlink", {})) as WhatsAppStatus,
    onSuccess: (status) => {
      setPending(null);
      queryClient.setQueryData(KEY, status);
      toast.success(t("WhatsApp disconnected"));
    },
  });

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  const help = (
    <>
      <div>
        <Button size="sm" variant="ghost" icon={Question} onClick={() => setHelpOpen(true)}>
          {t("How does this work?")}
        </Button>
      </div>
      <WhatsAppHelp
        open={helpOpen}
        onOpenChange={setHelpOpen}
        status={
          data
            ? {
                configured: data.configured,
                number: data.number,
                templateConfigured: data.templateConfigured,
                linked: data.linked,
                phone: data.phone,
              }
            : null
        }
      />
    </>
  );

  if (!data?.configured) {
    return (
      <div className="flex flex-col gap-3">
        <Text variant="small">
          {t(
            "WhatsApp is not set up on this server yet. An administrator needs to connect a WhatsApp Business number.",
          )}
        </Text>
        {help}
      </div>
    );
  }

  if (data.linked) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 rounded-md border border-divider p-3">
          <div className="min-w-0">
            <p className="truncate text-body font-medium text-text">
              {data.profileName
                ? t("Connected as {{name}} ({{phone}})", {
                    name: data.profileName,
                    phone: ltr(data.phone),
                  })
                : t("Connected as {{phone}}", { phone: ltr(data.phone) })}
            </p>
            {data.linkedAt ? (
              <p className="text-small text-text-tertiary">
                {t("Since {{when}}", { when: formatRelativeTime(data.linkedAt, i18n.language) })}
              </p>
            ) : null}
          </div>
          <Button
            size="sm"
            variant="ghost"
            disabled={unlink.isPending}
            onClick={() => unlink.mutate()}
          >
            {t("Disconnect")}
          </Button>
        </div>
        <Text variant="small">
          {data.windowOpenUntil
            ? t(
                "Questions arrive in WhatsApp as cards until {{when}} — tap an option, or reply to the message, to answer.",
                { when: new Date(data.windowOpenUntil).toLocaleString(i18n.language) },
              )
            : data.templateConfigured
              ? t(
                  "WhatsApp lets Docit write freely only for 24 hours after your last message. Until you write again, a question arrives as a short notice — reply to it and the full question follows.",
                )
              : t(
                  "WhatsApp lets Docit write freely only for 24 hours after your last message. Send any message (for example “inbox”) to receive what is waiting.",
                )}
        </Text>
        {help}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Text variant="small">
        {t(
          "Get the questions that are waiting for you in WhatsApp, and answer them with a tap or a reply.",
        )}
      </Text>
      {pending ? (
        <div className="flex flex-col gap-2 rounded-md border border-divider p-3">
          <a
            href={pending.url}
            target="_blank"
            rel="noreferrer"
            className="text-body font-medium text-accent hover:underline"
          >
            {t("Open WhatsApp and press Send")}
          </a>
          {pending.code && data.number ? (
            <p className="text-small text-text-secondary">
              {t("On another phone? Send this message to {{number}}:", {
                number: ltr(data.number),
              })}{" "}
              <code dir="ltr" className="rounded bg-bg-tertiary px-1 font-mono">
                link {pending.code}
              </code>
            </p>
          ) : null}
          <p className="text-small text-text-tertiary">
            {t("This link works once, for 15 minutes. This page updates when you are connected.")}
          </p>
        </div>
      ) : null}
      <div>
        <Button
          variant={pending ? "ghost" : "primary"}
          disabled={link.isPending}
          onClick={() => link.mutate()}
        >
          {pending ? t("Make a new link") : t("Connect WhatsApp")}
        </Button>
      </div>
      {help}
    </div>
  );
}
